/**
 * BESTIX v5.0 — Event Card
 * Photography-first event cards with heat indicators
 */

import { Link } from 'react-router-dom';
import { cn, formatPrice } from '@/lib/utils';
import { HeatIndicator } from '@/components/ui/HeatIndicator';

// ============================================
// Types
// ============================================

export interface EventCardProps {
  id: string | number;
  title: string;
  subtitle?: string;
  venue: string;
  location: string;
  dateTime: Date | string;
  priceFrom: number;
  ticketsAvailable?: number;
  heatLevel?: 1 | 2 | 3 | 4 | 5;
  imageUrl: string;
  category?: 'CONCERTS' | 'SPORTS' | 'THEATER' | 'FESTIVALS';
  href?: string;
  variant?: 'default' | 'compact' | 'featured';
}

// ============================================
// Helper Functions
// ============================================

function formatEventDate(dateTime: Date | string): string {
  // If it's already a formatted string (contains ·), return as-is
  if (typeof dateTime === 'string' && dateTime.includes('·')) {
    return dateTime;
  }
  
  const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
  
  // Check for invalid date
  if (isNaN(date.getTime())) {
    return typeof dateTime === 'string' ? dateTime : 'Date TBA';
  }
  
  const day = date.toLocaleDateString('en-US', { weekday: 'short' });
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const dayNum = date.getDate();
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day}, ${month} ${dayNum} · ${time}`;
}

// ============================================
// Event Card Component
// ============================================

export function EventCard({
  id,
  title,
  subtitle,
  venue,
  location,
  dateTime,
  priceFrom,
  ticketsAvailable,
  heatLevel = 3,
  imageUrl,
  category,
  href,
  variant = 'default',
}: EventCardProps) {
  const eventUrl = href || `/events/${id}`;
  const isHot = heatLevel >= 4;
  const isLowInventory = ticketsAvailable !== undefined && ticketsAvailable < 20;

  return (
    <Link
      to={eventUrl}
      className={cn(
        'group block bg-white rounded-xl overflow-hidden shadow-md transition-all duration-300',
        'hover:shadow-xl hover:-translate-y-1',
        variant === 'featured' && 'md:col-span-2',
        variant === 'compact' && 'flex flex-row'
      )}
    >
      {/* Image */}
      <div className={cn(
        'relative overflow-hidden',
        variant === 'compact' ? 'w-32 h-32 flex-shrink-0' : 'aspect-[4/3]'
      )}>
        <img
          src={imageUrl}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {category && variant !== 'compact' && (
          <span className="absolute top-3 left-3 px-2 py-1 bg-white/90 backdrop-blur-sm rounded text-xs font-medium text-[var(--color-navy)] uppercase tracking-wide">
            {category}
          </span>
        )}
        {(isHot || isLowInventory) && (
          <span className={cn(
            'absolute top-3 right-3 px-2 py-1 rounded text-xs font-bold text-white',
            isHot ? 'bg-[var(--color-hot)]' : 'bg-[var(--color-featured)]'
          )}>
            {isHot ? 'HOT' : 'LOW TICKETS'}
          </span>
        )}
      </div>

      {/* Content */}
      <div className={cn('p-4', variant === 'compact' && 'flex-1 py-3')}>
        <h3 className={cn(
          'font-playfair font-medium text-[var(--color-navy)] line-clamp-1',
          variant === 'compact' ? 'text-base' : 'text-xl'
        )}>
          {title}
        </h3>
        {subtitle && (
          <p className={cn(
            'font-playfair italic text-gray-600 mt-0.5 line-clamp-1',
            variant === 'compact' ? 'text-sm' : 'text-base'
          )}>
            {subtitle}
          </p>
        )}
        <p className={cn('text-gray-500 mt-2 line-clamp-1', variant === 'compact' ? 'text-xs' : 'text-sm')}>
          {venue}
        </p>
        <p className={cn('text-gray-400 line-clamp-1', variant === 'compact' ? 'text-xs' : 'text-sm')}>
          {location}
        </p>
        <p className={cn('text-[var(--color-navy)] font-medium mt-3', variant === 'compact' ? 'text-xs' : 'text-sm')}>
          {formatEventDate(dateTime)}
        </p>

        <div className="flex items-center justify-between mt-3">
          <div>
            <span className="text-[10px] text-gray-400 uppercase tracking-wide">From</span>
            <p className="text-[var(--color-navy)] font-semibold text-lg">{formatPrice(priceFrom)}</p>
          </div>
          {variant !== 'compact' && (
            <div className="text-right">
              <HeatIndicator level={heatLevel} />
              <p className="text-xs text-gray-400 mt-1">
                {heatLevel >= 4 ? 'High Demand' : heatLevel >= 2 ? 'Available' : 'Low Demand'}
              </p>
            </div>
          )}
        </div>

        {variant !== 'compact' && (
          <button className={cn(
            'w-full mt-4 py-3 rounded-full text-sm font-medium transition-colors',
            'bg-[var(--color-navy)] text-white hover:bg-[var(--color-navy-light)]',
            'group-hover:bg-[var(--color-gold)] group-hover:text-[var(--color-navy)]'
          )}>
            View Tickets
          </button>
        )}
      </div>
    </Link>
  );
}

// ============================================
// Event Card Grid
// ============================================

interface EventCardGridProps {
  events: EventCardProps[];
  columns?: 2 | 3 | 4;
  variant?: 'default' | 'compact';
  viewAllHref?: string;
  className?: string;
}

export function EventCardGrid({
  events,
  columns = 3,
  variant = 'default',
  viewAllHref,
  className,
}: EventCardGridProps) {
  const gridCols = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-2 lg:grid-cols-3',
    4: 'md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  };

  return (
    <div className={className}>
      <div className={cn(
        'grid gap-6',
        variant === 'compact' ? 'grid-cols-1' : `grid-cols-1 ${gridCols[columns]}`
      )}>
        {events.map((event) => (
          <EventCard key={event.id} {...event} variant={variant} />
        ))}
      </div>

      {viewAllHref && (
        <div className="text-center mt-8">
          <Link 
            to={viewAllHref} 
            className="inline-flex items-center gap-2 text-[var(--color-navy)] hover:text-[var(--color-gold)] font-medium"
          >
            View All
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      )}
    </div>
  );
}

export default EventCard;
